import { Controller } from "@hotwired/stimulus";
import { releaseNano } from "../lib/demo_nano_session";

export default class extends Controller {
  static values = { state: String };

  connect() {
    this.element.querySelector("[data-completion-dialog]")?.showModal();
    if (["complete", "declined", "support"].includes(this.stateValue))
      releaseNano();
    if (this.stateValue === "analyzing") {
      this.poll = setTimeout(() => this.refresh(), 1500);
    }
  }

  closeCompletion() {
    this.element.querySelector("[data-completion-dialog]")?.close();
  }

  disconnect() {
    clearTimeout(this.poll);
    this.disconnected = true;
  }

  sendOnEnter(event) {
    if (event.key !== "Enter" || event.shiftKey || event.isComposing || event.keyCode === 229) return;
    event.preventDefault();
    const input = event.currentTarget;
    if (event.repeat || this.pending || this.sending || input.readOnly || !input.value.trim()) return;
    const sendButton = input.form.querySelector('.ft-button[type="submit"]');
    if (sendButton && !sendButton.disabled) input.form.requestSubmit(sendButton);
  }

  composeEmotion(event) {
    this.composeSuggestion(event);
  }

  composeSuggestion(event) {
    if (this.pending || this.sending) return;
    const pill = event.currentTarget;
    const input = this.element.querySelector('textarea[name="draft_check_in[message]"]');
    const current = input.value.trimEnd();
    const label = pill.dataset.phrase || pill.dataset.emotion;
    const separator = !current ? "" : current === this.emotionDraft ? ", " : /[.!?]$/.test(current) ? " " : ". ";
    const word = !current || separator !== ", " ? label.charAt(0).toUpperCase() + label.slice(1) : label;
    const next = current + separator + word;
    if (next.length > input.maxLength) {
      input.focus();
      return;
    }
    input.value = next;
    this.emotionDraft = next;
    input.dispatchEvent(new Event("input", { bubbles: true }));
    const row = pill.parentElement;
    for (const emotion of JSON.parse(pill.dataset.secondary || "[]")) {
      if (Array.from(row.querySelectorAll("[data-emotion]")).some((el) => el.dataset.emotion === emotion)) continue;
      const suggestion = document.createElement("button");
      suggestion.type = "button";
      suggestion.className = "ft-quick-reply ft-emotion-enter";
      suggestion.dataset.action = "conversation#composeEmotion";
      suggestion.dataset.emotion = emotion;
      suggestion.textContent = emotion.charAt(0).toUpperCase() + emotion.slice(1);
      pill.before(suggestion);
    }
    pill.remove();
    input.focus();
    input.setSelectionRange(next.length, next.length);
  }

  start(event) {
    const form = event.target;
    if (this.pending) return;
    this.pending = true;
    const dock = this.element.querySelector(".ft-reply-dock");
    dock.style.minHeight = `${dock.getBoundingClientRect().height}px`;
    const text =
      form.dataset.chatReply ||
      form.querySelector('[name="draft_check_in[message]"]')?.value.trim() ||
      form.querySelector('[name="draft_check_in[free_response]"]')?.value.trim() ||
      (["need_pick", "needs_done"].includes(form.querySelector('[name="commit"]')?.value) ? form.querySelector('button[type="submit"]')?.textContent.trim() : "") ||
      (form.querySelector('[name="commit"]')?.value === "answer" && form.querySelector('[name="answer"]') ? form.querySelector('button[type="submit"]')?.textContent.trim() : "") ||
      Array.from(form.querySelectorAll("input:checked"))
        .map((input) => input.closest("label").textContent.trim())
        .join(", ") ||
      (form.querySelector('[name="commit"]')?.value === "answer"
        ? "Nothing right now"
        : form.querySelector('[name="commit"]')?.value === "finish"
          ? "That feels right."
          : form.querySelector('[name="commit"]')?.value === "skip"
            ? "Skip for now"
            : "");
    this.element.querySelectorAll("dialog[open]").forEach((dialog) => dialog.close());
    const thread = this.element.querySelector(".ft-thread");
    if (text) {
      const message = document.createElement("div");
      message.className = "ft-message mine ft-pending-message";
      const bubble = document.createElement("p");
      bubble.className = "ft-bubble mine";
      bubble.textContent = text;
      message.append(bubble);
      thread.append(message);
    }
    const typing = document.createElement("div");
    typing.className = "ft-message ft-pending-message";
    typing.innerHTML =
      '<div class="ft-bubble ft-reading" role="status" tabindex="0"><span class="ft-dots" aria-hidden="true"><i></i><i></i><i></i></span></div>';
    thread.append(typing);
    this.element.classList.add("ft-waiting");
    this.progress({ detail: { message: form.querySelector('[name="commit"]')?.value === "analyze_message" ? "Reading your response…" : "Saving your reply…" } });
    this.element.querySelector(".ft-transcript").scrollTo({
      top: thread.scrollHeight,
      behavior: matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "instant"
        : "smooth",
    });
  }

  progress(event) {
    const debugStatus = document.querySelector(".ft-debug-status");
    if (debugStatus) debugStatus.textContent = event.detail.message;
    const indicator = this.element.querySelector(
      ".ft-pending-message .ft-reading",
    );
    if (!indicator) return;
    indicator.title = event.detail.message;
    indicator.setAttribute("aria-label", event.detail.message);
    indicator.dataset.detail = event.detail.message;
  }

  failure(event) {
    this.pending = false;
    this.element.querySelector(".ft-reply-dock").style.minHeight = "";
    this.element.classList.remove("ft-waiting");
    this.element
      .querySelectorAll(".ft-pending-message")
      .forEach((el) => el.remove());
    let error = this.element.querySelector(".ft-chat-error");
    if (!error) {
      error = document.createElement("p");
      error.className = "ft-chat-error";
      error.setAttribute("role", "alert");
      this.element.querySelector(".ft-reply-inner").prepend(error);
    }
    error.textContent = event.detail.message;
  }

  async submit(event) {
    if (event.defaultPrevented) return; // Nano's controller will submit once ready.
    const form = event.target;
    if (
      form.method.toLowerCase() !== "post" ||
      !form.querySelector('[name="_method"][value="patch"]')
    )
      return;
    event.preventDefault();
    if (this.pending && !form.querySelector('[name="browser_analysis"]')?.value)
      return;
    if (this.sending) return;
    this.sending = true;
    this.sentAt = performance.now();
    this.element.querySelector(".ft-chat-error")?.remove();
    if (form.querySelector('[name="commit"]')?.value !== "edit_answer" && !form.dataset.chatBack)
      this.start({ target: form });
    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form, event.submitter),
        credentials: "same-origin",
      });
      await this.replace(response);
    } catch {
      this.failure({
        detail: {
          message:
            "Couldn't save your reply. Your text is still here. Try again.",
        },
      });
      this.sending = false;
    }
  }

  async refresh() {
    try {
      await this.replace(
        await fetch(window.location.href, { credentials: "same-origin" }),
      );
    } catch {
      if (!this.disconnected)
        this.poll = setTimeout(() => this.refresh(), 3000);
    }
  }

  async replace(response) {
    const html = new DOMParser().parseFromString(
      await response.text(),
      "text/html",
    );
    if (this.disconnected) return;
    if (!response.ok) throw new Error("Save failed");
    if (this.sentAt)
      await new Promise((resolve) =>
        setTimeout(
          resolve,
          Math.max(0, 320 - (performance.now() - this.sentAt)),
        ),
      );
    if (this.disconnected) return;
    const next = html.querySelector(".ft-conversation");
    if (!next) {
      const destination = new URL(response.url);
      if (destination.origin === window.location.origin && destination.pathname.startsWith("/students/draft_check_ins/")) {
        window.location.assign(destination.href);
        return;
      }
      throw new Error("Missing conversation");
    }
    const destination = new URL(response.url);
    if (destination.origin === window.location.origin &&
        (destination.pathname.startsWith("/demo/free-text/") || destination.pathname.startsWith("/students/draft_check_ins/"))) {
      window.history.replaceState({}, "", destination.href);
    }
    const previous = new Set(
      Array.from(this.element.querySelectorAll(".ft-bubble")).map((node) =>
        node.textContent.trim(),
      ),
    );
    next.querySelectorAll(".ft-message").forEach((message) => {
      if (previous.has(message.querySelector(".ft-bubble")?.textContent.trim()))
        message.classList.add("ft-settled");
    });
    const inspector = document.querySelector(".ft-debug");
    const nextInspector = html.querySelector(".ft-debug");
    if (inspector && nextInspector) {
      const expanded = new Map(
        Array.from(inspector.querySelectorAll("[data-debug-id]")).map((el) => [el.dataset.debugId, el.open]),
      );
      nextInspector.querySelector(".ft-debug-panel").open = inspector.querySelector(".ft-debug-panel").open;
      nextInspector.querySelectorAll("[data-debug-id]").forEach((el) => {
        if (expanded.has(el.dataset.debugId)) el.open = expanded.get(el.dataset.debugId);
      });
      const scrollTop = inspector.querySelector(".ft-debug-feed").scrollTop;
      inspector.replaceWith(nextInspector);
      nextInspector.querySelector(".ft-debug-feed").scrollTop = scrollTop;
    }
    this.element.replaceWith(next);
  }
}
